import { IAddress } from './address';
import { IAgency } from './agency';
import { IAuditModel } from './base';
import { IFile } from './file';
import { IInventoryLocation } from './inventory.locations';
import { IKase, IOffense, IOffenseOptionItem } from './kase';
import { IMasterName } from './master.names';
import { IAlcohol } from './property.alcohol';
import { ICash } from './property.cash';
import { ICreditCard } from './property.creditcard';
import { IDrug } from './property.drug';
import { IExplosive } from './property.explosive';
import { IFirearm } from './property.firearm';
import { IJewelry } from './property.jewelry';
import { IKnife } from './property.knife';
import { IUser } from './user';
import { IMasterVehicle, VehicleInvolvement } from './vehicle';
import { IVendor } from './vendors';

export enum PropertyTypeTag {
  PHYSICAL = 'Physical',
  VEHICLE = 'Vehicle',
  DIGITAL = 'Digital',
}

export const PropertyTypeTagList = {
  [PropertyTypeTag.PHYSICAL]: 'Physical',
  [PropertyTypeTag.VEHICLE]: 'Vehicle',
  [PropertyTypeTag.DIGITAL]: 'Digital',
} as const;

export const RelatedEntriesNames = (
  VehicleType: Map<string, IOffenseOptionItem>,
  PropertyTypePhysical: Map<string, IOffenseOptionItem>,
) => {
  if (!VehicleType.size || !PropertyTypePhysical.size) {
    return {};
  }
  return {
    vehicle: [
      VehicleType.get('AIRCRAFT')?.key,
      VehicleType.get('AUTOMOBILES')?.key,
      VehicleType.get('BICYCLES')?.key,
      VehicleType.get('BUSES')?.key,
      VehicleType.get('TRUCKS')?.key,
      VehicleType.get('WATERCRAFT')?.key,
      VehicleType.get('RECREATIONAL_VEHICLES')?.key,
      VehicleType.get('OTHER_MOTOR_VEHICLES')?.key,
    ],
    alcohol: PropertyTypePhysical?.get('ALCOHOL')?.key,
    creditCard: PropertyTypePhysical?.get('CREDIT_DEBIT_CARDS')?.key,
    explosive: PropertyTypePhysical?.get('EXPLOSIVES')?.key,
    cash: PropertyTypePhysical?.get('MONEY')?.key,
    drugs: PropertyTypePhysical?.get('DRUGS_NARCOTICS')?.key,
    jewelry: PropertyTypePhysical?.get('JEWELRY_PRECIOUS_METALS')?.key,
    knife: PropertyTypePhysical?.get('OTHER_WEAPONS')?.key,
    firearm: PropertyTypePhysical?.get('FIREARMS')?.key,
  };
};
// export type RelatedEntryNamesType = keyof typeof VehicleType &
//   keyof typeof PropertyTypePhysical;
//
export enum PropertyStatus {
  AUCTIONED = 'AUCTIONED',
  DESTROYED = 'DESTROYED',
  DISPOSED = 'DISPOSED',
  IN_POLICE_CUSTODY = 'IN_POLICE_CUSTODY',
  LOGGED = 'LOGGED',
  RELEASED_TO_COURT = 'RELEASED_TO_COURT',
  RELEASED_TO_LAB = 'RELEASED_TO_LAB',
  RELEASED_TO_OWNER = 'RELEASED_TO_OWNER',
  RELEASED_TO_PERSON = 'RELEASED_TO_PERSON',
  // External transfer to other agencies
  TRANSFERRED = 'TRANSFERRED',
}

export const PropertyStatusList = new Map([
  [PropertyStatus.LOGGED, 'Logged'],
  [PropertyStatus.RELEASED_TO_OWNER, 'Released to owner'],
  [PropertyStatus.RELEASED_TO_COURT, 'Released to court'],
  [PropertyStatus.RELEASED_TO_LAB, 'Released to lab'],
  [PropertyStatus.RELEASED_TO_PERSON, 'Released to person'],
  [PropertyStatus.IN_POLICE_CUSTODY, 'In police custody'],
  [PropertyStatus.DESTROYED, 'Destroyed'],
  [PropertyStatus.DISPOSED, 'Disposed'],
  [PropertyStatus.AUCTIONED, 'Auctioned'],
  // External transfer to other agencies
  [PropertyStatus.TRANSFERRED, 'Transferred'],
]);

export interface IPropertyLoggedInfo {
  loggedBy?: IUser;
  loggedDate?: Date;
  loggedAddress?: IAddress;
  isLoggedAtKaseAddress?: boolean;
}

export interface IInboundTransferredInfo {
  transferredBy?: IUser;
  transferredDate?: Date;
}

export enum PropertyVehicleInvolvement {
  ABANDONED = 'ABANDONED',
  ACCIDENT = 'ACCIDENT',
  ARREST = 'ARREST',
  IMPOUNDED = 'IMPOUNDED',
  TOWED = 'TOWED',
}

export interface IPropertyVehicle {
  dataSourceVerified?: boolean;
  masterVehicle?: IMasterVehicle;
  involvement?: PropertyVehicleInvolvement;
  // Towing company type vendor
  vendor?: IVendor;
  parkingSpotNumber?: string;
  impoundLot?: string;
}
export interface IPropertyVehicleForm
  extends Omit<IPropertyVehicle, 'masterVehicle' | 'vendor'> {
  masterVehicle?: string;
  vendor?: string;
}

export interface IPropertyVehicleParts {
  inventoryLocation?: IInventoryLocation;
}
export interface relatedBase {
  notes?: string;
}
export interface IPropertyRelatedOffenses extends relatedBase {
  offenses?: IOffense[];
}

export interface IPropertyRelatedMasterNames extends relatedBase {
  masterNames?: IMasterName[];
}

export interface IPropertyRelatedMasterVehicles extends relatedBase {
  masterVehicles?: IMasterVehicle[];
}

export interface IBaseCustodyActionResultInfo {
  actionDateTime?: Date;
  notes?: string;
  officer?: IUser;
  updatedAt?: Date;
  updatedBy?: IUser;
}

interface IBasePropertyReleaseInfo {
  expectedBackDate?: Date;
  signature?: string | IFile;
}

export interface IReleasedToLabInfo
  extends IBasePropertyReleaseInfo,
    IBaseCustodyActionResultInfo {
  // Lab type vendor
  vendor?: IVendor;
  trackingNumber?: string;
}

export interface IReleasedToCourtInfo
  extends IBasePropertyReleaseInfo,
    IBaseCustodyActionResultInfo {
  // Court type vendor
  vendor?: IVendor;
  trackingNumber?: string;
}

export interface IReleasedToPersonInfo
  extends IBasePropertyReleaseInfo,
    IBaseCustodyActionResultInfo {
  // user of current agency
  user?: IUser;
  // masterName person involved in this incident
  masterName?: IMasterName;
}

export interface IInPoliceCustodyInfo extends IBaseCustodyActionResultInfo {
  inventoryLocation?: IInventoryLocation;
}

// transfer to other agencies
export interface IOutboundTransferredInfo
  extends IBaseCustodyActionResultInfo,
    IBasePropertyReleaseInfo {
  agency?: IAgency;
  user?: IUser;
  trackingNumber?: string;
}

export type ICustodyActionResultInfo =
  | IBaseCustodyActionResultInfo
  | IInPoliceCustodyInfo
  | IReleasedToLabInfo
  | IReleasedToCourtInfo
  | IReleasedToPersonInfo
  | IOutboundTransferredInfo;

export interface IPropertyTypes {
  alcohol?: IAlcohol;
  cash?: ICash;
  creditCard?: ICreditCard;
  drugs?: IDrug[];
  explosive?: IExplosive;
  firearm?: IFirearm;
  jewelry?: IJewelry;
  knife?: IKnife;
  vehicle?: IPropertyVehicle;
}

export interface IProperty extends IAuditModel, IPropertyTypes {
  drugsDataSource?: string;
  itemNumber?: string;
  createdBy: IUser;
  custodyActionResultInfo?: ICustodyActionResultInfo;
  description?: string;
  digitalFile?: IFile;
  droppedOffLocation?: IInventoryLocation;
  files?: IFile[];
  inboundTransferredInfo?: IInboundTransferredInfo;
  involvement?: string;
  quantity?: string;
  isBiohazard?: boolean;
  isEvidence?: boolean;
  kaseId: IKase;
  labResults?: IFile[];
  loggedInfo?: IPropertyLoggedInfo;
  kase?: string;
  name?: string;
  number: string;
  owner?: IMasterName;
  // recoveryDate deprecated - superseded by loggedInfo.loggedDate
  // recoveryDate?: string | Date | null;
  relatedMasterNames?: IPropertyRelatedMasterNames;
  relatedMasterVehicles?: IPropertyRelatedMasterVehicles;
  relatedOffenses?: IPropertyRelatedOffenses;
  serialNumber?: string;
  status: PropertyStatus;
  targetDispositionDate?: string;
  type?: string;
  typeTag: PropertyTypeTag;
  value?: number;
  vehicleParts?: IPropertyVehicleParts;
}

export interface IPropertyForm
  extends Omit<
    IProperty,
    | 'kaseId'
    | 'owner'
    | 'firearm'
    | 'jewelry'
    | 'knife'
    | 'alcohol'
    | 'inboundTransferredInfo'
    | 'relatedMasterNames'
    | 'relatedMasterVehicles'
    | 'relatedOffenses'
    | 'loggedInfo'
    | 'vehicle'
    | 'droppedOffLocation'
  > {
  address?: IAddress;
  owner?: string;
  propertyTypeDetails: string;
  vehicleInvolvement: keyof typeof VehicleInvolvement;
  droppedOffLocation: string;
  inPoliceCustody?: boolean;
  loggedInfo: {
    loggedBy?: string;
    loggedDate?: string;
    loggedAddress?: IAddress;
    isLoggedAtKaseAddress?: boolean;
  };
  dataSource: string; // <-- new line added
  inboundTransferredInfo?: {
    transferredBy?: string;
    transferredDate?: string;
  };
  jewelry?: {
    type: string;
  };
  firearm?: {
    model: string;
    make: string;
  };
  knife?: {
    model: string;
    make: string;
  };
  alcohol?: {
    brand: string;
  };
  relatedMasterNames?: {
    notes: string;
    masterNames: string[];
  };
  relatedMasterVehicles?: {
    notes: string;
    masterVehicles: string[];
  };
  relatedOffenses?: {
    notes: string;
    offenses: string[];
  };
  vehicle: IPropertyVehicleForm;
}

// recoveryDate deprecated - superseded by loggedInfo.loggedDate
export interface IPropertyRequest extends Omit<IPropertyForm, 'loggedInfo'> {
  loggedInfo: {
    loggedBy?: string;
    loggedDate?: string;
    loggedAddress?: string;
    isLoggedAtKaseAddress?: boolean;
  };
  // recoveryDate?: string | Date | null;
}
